// THE MULTIVIEW XR PATH (quest-multiview, 2026-11-02; APP_SURFACE_PERF "The XR path", APP_PLAYER §VR):
// both eyes in ONE pass (OVR_multiview2) on a 'texture-array' XRProjectionLayer, and the eyes'
// depth submitted with it (positional reprojection) — the controller the vendored three patch
// (client/vendor/three-r170-xr.cjs) calls as renderer.multiview. Device-class terms
// (device.js CLASS_ON): `xr_multiview` (this path at all) and `xr_depth` (the layer gets a
// depth format and the eye depth is resolved into it; off = an own depth array, invalidated
// after the frame). Every other class never installs it: three's own per-eye path, bit for bit.
//
// MSAA: the eye target is multisampled-render-to-texture (the sample count per frame: the Quest picture
// profile's, questprof.js; 4 = three's own) (framebufferTextureMultisample-
// MultiviewOVR, OCULUS_multiview / OVR_multiview2 on the Quest); a context without it — or
// without OVR_multiview2 — is never `wanted` (three's 4× MSAA per-eye path keeps the look).
//
// SPACE WARP (spacewarp.js, opts.sw; APP_SURFACE_PERF "Space warp"): only when the session GRANTED 'space-warp'
// (sw.begin at the layer's creation) — the layer gets DEPTH_COMPONENT24, the colour pass keeps its OWN depth (the
// layer's depth is motion-vector sized then) un-invalidated, and frameEnd runs sw.pass(that depth). Not granted /
// no sw: every line below as before.
//
//   createMultiview(renderer, { on, depth, log, sw }) -> controller (install: renderer.multiview = it)
//     .want(bool)        the session about to start is a VR one (main.js launch) → wanted
//     .wanted / .views   read by the patch (program variants: compile() builds the mv one)
//     .createLayer / .target / .frame / .frameEnd / .end   the patch's XR hooks
//     .state()           the facts (`__vr.mv()`, the session-start `[xr]` line)
//     .smoke(scene, camera, o)   THE SMOKE: the scene through an own 2-layer multiview target vs
//                        each eye on its own — both views drawn, every program's link, pixels
//
import * as THREE from 'three';
import { questSamples } from './questprof.js';

const VIEWS = 2;
const SAMPLES = 4;   // three's XR eye target (WebXRManager: antialias ? 4 : 0)
// the eye target's MSAA per frame: the Quest picture profile's (questprof.js: 4 / 2 / 0, the owner's
// switch, live — the attachments are re-attached every frame anyway), else SAMPLES
const samplesNow = () => { const q = questSamples(); return q == null ? SAMPLES : q; };

// the GL multiview entry points of a context: { mv: framebufferTextureMultiviewOVR host,
// ms: framebufferTextureMultisampleMultiviewOVR host | null }
export function multiviewExt(gl) {
  const mv = gl.getExtension('OVR_multiview2');
  if (!mv || typeof mv.framebufferTextureMultiviewOVR !== 'function') return null;
  const oc = gl.getExtension('OCULUS_multiview');
  const ms = mv.framebufferTextureMultisampleMultiviewOVR ? mv
    : (oc && typeof oc.framebufferTextureMultisampleMultiviewOVR === 'function' ? oc : null);
  return { mv, ms, maxViews: gl.getParameter(mv.MAX_VIEWS_OVR || 0x9631) };
}

// attach a color (+ depth) TEXTURE_2D_ARRAY's views [0, VIEWS) to the bound framebuffer (no status read:
// checkFramebufferStatus is a synchronous round trip, the caller asks only when the attachment set is new)
function attach(gl, ext, color, depth, samples, depthAtt) {
  const F = gl.FRAMEBUFFER;
  if (samples > 0) {
    ext.ms.framebufferTextureMultisampleMultiviewOVR(F, gl.COLOR_ATTACHMENT0, color, 0, samples, 0, VIEWS);
    if (depth) ext.ms.framebufferTextureMultisampleMultiviewOVR(F, depthAtt, depth, 0, samples, 0, VIEWS);
  } else {
    ext.mv.framebufferTextureMultiviewOVR(F, gl.COLOR_ATTACHMENT0, color, 0, 0, VIEWS);
    if (depth) ext.mv.framebufferTextureMultiviewOVR(F, depthAtt, depth, 0, 0, VIEWS);
  }
}

function arrayTex(gl, internal, w, h) {
  const t = gl.createTexture();
  gl.bindTexture(gl.TEXTURE_2D_ARRAY, t);
  gl.texStorage3D(gl.TEXTURE_2D_ARRAY, 1, internal, w, h, VIEWS);
  gl.texParameteri(gl.TEXTURE_2D_ARRAY, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
  gl.texParameteri(gl.TEXTURE_2D_ARRAY, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
  gl.bindTexture(gl.TEXTURE_2D_ARRAY, null);
  return t;
}

export function createMultiview(renderer, { on = false, depth = false, log = console.info, sw = null } = {}) {
  const gl = renderer.getContext();
  let ext;                       // undefined = not asked yet (the extension is asked lazily: no desktop driver call)
  const extOf = () => (ext === undefined ? (ext = multiviewExt(gl)) : ext);
  let antialias = true;
  let cur = null;                // { fb, color, depth, submit } the session's ONE multiview framebuffer
  let own = null;                // { tex, w, h } the own depth array (no depth submit)
  let facts = null;
  let frames = 0;

  const c = {
    views: VIEWS,
    on, depth, sw,
    wanted: false,
    // MSAA needs the multisampled multiview attach; a context without it keeps three's path
    supported() {
      const e = on ? extOf() : null;
      antialias = !!(gl.getContextAttributes() || {}).antialias;
      return !!(e && e.maxViews >= VIEWS && (!antialias || e.ms));
    },
    want(v) { c.wanted = !!v && c.supported(); return c.wanted; },

    // WebXRManager: the projection layer (null = declined: three creates its own)
    createLayer(binding, init, attributes) {
      if (!c.wanted) return null;
      // space warp granted: the layer's depth is the motion vector pass's (DEPTH_ATTACHMENT, no stencil)
      const swOn = !!(sw && sw.begin(renderer.xr.getSession()));
      const dFmt = swOn ? gl.DEPTH_COMPONENT24 : depth ? (attributes.stencil ? gl.DEPTH24_STENCIL8 : gl.DEPTH_COMPONENT24) : 0;
      let layer = null;
      try {
        layer = binding.createProjectionLayer({ ...init, textureType: 'texture-array', depthFormat: dFmt });
      } catch (e) {
        facts = { active: false, declined: String(e && e.message || e) };
        if (sw) sw.end();
        log(`[xr] multiview declined: createProjectionLayer('texture-array') threw — ${facts.declined}; three's per-eye path`);
        return null;
      }
      facts = { active: true, textureType: layer.textureType, tw: layer.textureWidth, th: layer.textureHeight,
        depthFormat: dFmt, ignoreDepthValues: layer.ignoreDepthValues, samples: antialias ? samplesNow() : 0, status: null, fbs: 0, ownDepth: false };
      log(`[xr] multiview ON: ${VIEWS} views ${layer.textureWidth}×${layer.textureHeight} ${layer.textureType}, ${facts.samples}× MSAA, depth ${swOn ? 'own (space warp: read by the motion vector pass)' : dFmt ? 'submitted' : 'own (invalidated)'}, ignoreDepthValues ${layer.ignoreDepthValues}`);
      return layer;
    },

    // WebXRManager: the frame's target (its framebuffer set per frame by frame())
    target(layer, attributes) {
      const rt = new THREE.WebGLRenderTarget(layer.textureWidth, layer.textureHeight, {
        format: THREE.RGBAFormat, type: THREE.UnsignedByteType, colorSpace: renderer.outputColorSpace,
        depthBuffer: true, stencilBuffer: !!attributes.stencil, samples: 0 });
      rt.multiview = true;
      return rt;
    },

    // WebXRManager, view 0 of each frame: the swapchain image, attached to the session's ONE framebuffer EVERY
    // frame. The Quest Browser (144) hands back the SAME WebGLTexture object for sub.colorTexture each frame and
    // re-points it to the next swapchain image underneath: a framebuffer attached once per texture object kept
    // rendering into a stale image — black in the headset at ~9 fps, and the context lost after the session
    // (measured on a Quest 3, APP_SURFACE_PERF "The XR path"). three's own per-eye path re-attaches per frame too.
    frame(sub, layer, rt) {
      const color = sub.colorTexture;
      if (sw && sw.granted) sw.frame(sub, layer);
      const submit = !(sw && sw.granted) && !layer.ignoreDepthValues && sub.depthStencilTexture ? sub.depthStencilTexture : null;
      const d = submit || ownDepth(layer.textureWidth, layer.textureHeight);
      if (!cur) cur = { fb: gl.createFramebuffer(), color: null, depth: null, samples: -1, submit: false };
      renderer.state.bindFramebuffer(gl.FRAMEBUFFER, cur.fb);
      const ns = antialias ? samplesNow() : 0;
      attach(gl, extOf(), color, d, ns,
        submit && facts && facts.depthFormat === gl.DEPTH24_STENCIL8 ? gl.DEPTH_STENCIL_ATTACHMENT : gl.DEPTH_ATTACHMENT);
      // the pass's sample count for its readers (vegmat.js targetMs: A2C only on a multisampled pass)
      rt.mvSamples = ns;
      if (facts) facts.samples = ns;
      if (cur.color !== color || cur.depth !== d || cur.samples !== ns) {   // a new attachment set: its status read once
        const st = gl.checkFramebufferStatus(gl.FRAMEBUFFER);
        if (facts) { facts.status = st === gl.FRAMEBUFFER_COMPLETE ? 'complete' : `0x${st.toString(16)}`; facts.fbs = 1; facts.submit = !!submit; }
        if (st !== gl.FRAMEBUFFER_COMPLETE) log(`[xr] multiview framebuffer INCOMPLETE (0x${st.toString(16)})`);
        cur.color = color; cur.depth = d; cur.samples = ns;
      }
      cur.submit = !!submit;
      c._cur = cur;
      renderer.setRenderTargetFramebuffer(rt, cur.fb);
    },

    // WebXRManager, after the frame: an eye depth nobody reads is discarded, never resolved
    frameEnd() {
      frames++;
      const e = c._cur;
      if (e && sw && sw.granted) { sw.pass(e.depth); return; }   // the pass reads the eye depth: never invalidated then
      if (!e || e.submit) return;
      renderer.state.bindFramebuffer(gl.FRAMEBUFFER, e.fb);
      gl.invalidateFramebuffer(gl.FRAMEBUFFER, [gl.DEPTH_ATTACHMENT]);
    },

    end() {
      if (cur) gl.deleteFramebuffer(cur.fb);
      cur = null;
      if (own) { gl.deleteTexture(own.tex); own = null; }
      if (sw) sw.end();
      c._cur = null;
      if (facts) { facts.active = false; facts.frames = frames; }
      log(`[xr] multiview session end: ${frames} frames`);
      frames = 0;
    },

    state() {
      const e = ext === undefined ? undefined : ext;
      return { on, depth, wanted: c.wanted, ext: e === undefined ? 'unasked' : !!e, ms: !!(e && e.ms), maxViews: e ? e.maxViews : 0,
        xr: renderer.xr.isMultiview ? renderer.xr.isMultiview() : false, frames, ...(facts || {}), ...(sw ? { sw: sw.state() } : {}) };
    },

    smoke: (scene, camera, o) => smoke(renderer, extOf(), scene, camera, o),
  };

  function ownDepth(w, h) {
    if (own && own.w === w && own.h === h) return own.tex;
    if (own) gl.deleteTexture(own.tex);
    own = { tex: arrayTex(gl, gl.DEPTH_COMPONENT24, w, h), w, h };
    if (facts) facts.ownDepth = true;
    return own.tex;
  }

  return c;
}

// ------------------------------------------------------------------------------------------
// THE SMOKE (no XR device needed: the GPU probe on this box's GPU, `__vr.mvTest()`): the scene
// rendered through an own 2-layer multiview target with an ArrayCamera of two eyes (IPD
// `ipd`, the head at `camera`), then each eye alone into a plain target. Returns per view the
// drawn pixels (non-clear) and the difference to that eye's own render, the draws / tris of
// one multiview pass vs the two eye passes, and every material's multiview program: its link
// status, its info logs, its name. Leaves the renderer on the canvas.
function smoke(renderer, ext, scene, camera, { w = 512, h = 512, ipd = 0.064, samples = 0, clear = 0x000000 } = {}) {
  const gl = renderer.getContext();
  if (!ext) return { ok: false, why: 'no OVR_multiview2' };
  const out = { ok: false, w, h, samples, ext: { maxViews: ext.maxViews, ms: !!ext.ms } };
  // the eyes: the head camera's pose and projection, ±ipd/2 along its own x
  camera.updateMatrixWorld();
  const eyes = [-ipd / 2, ipd / 2].map((dx) => {
    const e = camera.clone(false);   // not its children (the weapon rides the camera)
    e.matrixAutoUpdate = false;
    e.matrixWorld.copy(camera.matrixWorld).multiply(new THREE.Matrix4().makeTranslation(dx, 0, 0));
    e.matrixWorldInverse.copy(e.matrixWorld).invert();
    e.matrix.copy(e.matrixWorld);
    e.matrixWorldAutoUpdate = false;
    e.viewport = new THREE.Vector4(0, 0, w, h);
    return e;
  });
  const arr = new THREE.ArrayCamera(eyes);
  arr.projectionMatrix.copy(camera.projectionMatrix);
  arr.projectionMatrixInverse.copy(camera.projectionMatrixInverse);
  arr.matrixWorld.copy(camera.matrixWorld);
  arr.matrixWorldInverse.copy(camera.matrixWorldInverse);
  arr.matrixAutoUpdate = false; arr.matrixWorldAutoUpdate = false;
  arr.layers.mask = camera.layers.mask;   // three's XR eyes add layers 1 / 2 (unused by the client)

  const prevRt = renderer.getRenderTarget();
  const xrOn = renderer.xr.enabled;
  renderer.xr.enabled = false;
  const autoReset = renderer.info.autoReset;
  renderer.info.autoReset = true;
  const color = arrayTex(gl, gl.RGBA8, w, h), depth = arrayTex(gl, gl.DEPTH_COMPONENT24, w, h);
  const fb = gl.createFramebuffer();
  const rt = new THREE.WebGLRenderTarget(w, h, { colorSpace: renderer.outputColorSpace, samples: 0 });
  rt.multiview = true;
  rt.isXRRenderTarget = true;             // the XR target's colour space + tone map rule
  const ref = new THREE.WebGLRenderTarget(w, h, { colorSpace: renderer.outputColorSpace, samples: 0 });
  ref.isXRRenderTarget = true;
  // the XR layer's colour is RGBA8 (three's projectionlayerInit colorFormat), the shader encodes sRGB itself; an sRGB
  // + UnsignedByte target would get SRGB8_ALPHA8 here (a second, hardware encode) — the reference would be the odd one
  ref.texture.internalFormat = 'RGBA8';
  const readFb = gl.createFramebuffer();
  const px = () => new Uint8Array(w * h * 4);
  const mvPx = [px(), px()], refPx = [px(), px()];
  try {
    renderer.state.bindFramebuffer(gl.FRAMEBUFFER, fb);
    attach(gl, ext, color, depth, samples, gl.DEPTH_ATTACHMENT);
    const st = gl.checkFramebufferStatus(gl.FRAMEBUFFER);
    out.status = st === gl.FRAMEBUFFER_COMPLETE ? 'complete' : `0x${st.toString(16)}`;
    if (st !== gl.FRAMEBUFFER_COMPLETE) return out;
    renderer.setRenderTargetFramebuffer(rt, fb);
    renderer.setClearColor(clear, 1);
    // ONE multiview pass
    renderer.setRenderTarget(rt);
    renderer.clear();
    renderer.render(scene, arr);
    out.mv = { calls: renderer.info.render.calls, tris: renderer.info.render.triangles };
    renderer.state.bindFramebuffer(gl.FRAMEBUFFER, readFb);
    for (let v = 0; v < VIEWS; v++) {
      gl.framebufferTextureLayer(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, color, 0, v);
      gl.readPixels(0, 0, w, h, gl.RGBA, gl.UNSIGNED_BYTE, mvPx[v]);
    }
    // each eye alone (three's per-eye path, the same frustum: the ArrayCamera's culling)
    out.eyes = { calls: 0, tris: 0 };
    for (let v = 0; v < VIEWS; v++) {
      const one = new THREE.ArrayCamera([eyes[v]]);
      one.projectionMatrix.copy(arr.projectionMatrix); one.projectionMatrixInverse.copy(arr.projectionMatrixInverse);
      one.matrixWorld.copy(arr.matrixWorld); one.matrixWorldInverse.copy(arr.matrixWorldInverse);
      one.matrixAutoUpdate = false; one.matrixWorldAutoUpdate = false;
      one.layers.mask = arr.layers.mask;
      renderer.setRenderTarget(ref);
      renderer.clear();
      renderer.render(scene, one);
      out.eyes.calls += renderer.info.render.calls; out.eyes.tris += renderer.info.render.triangles;
      renderer.readRenderTargetPixels(ref, 0, 0, w, h, refPx[v]);
    }
    // the pixels: drawn (≠ clear) per view, and the difference to the eye's own render
    const cr = (clear >> 16) & 255, cg = (clear >> 8) & 255, cb = clear & 255;
    out.views = mvPx.map((a, v) => {
      const b = refPx[v];
      let drawn = 0, diff = 0, maxd = 0, sum = 0;
      for (let i = 0; i < a.length; i += 4) {
        if (a[i] !== cr || a[i + 1] !== cg || a[i + 2] !== cb) drawn++;
        const d = Math.max(Math.abs(a[i] - b[i]), Math.abs(a[i + 1] - b[i + 1]), Math.abs(a[i + 2] - b[i + 2]));
        if (d > 2) diff++;
        if (d > maxd) maxd = d;
        sum += a[i] + a[i + 1] + a[i + 2];
      }
      return { drawn, drawn_pct: +(100 * drawn / (w * h)).toFixed(2), diff_px: diff, diff_pct: +(100 * diff / (w * h)).toFixed(3), max_d: maxd, mean: +(sum / (3 * w * h)).toFixed(2) };
    });
    // the two views differ (two eyes, not one picture twice)
    let lr = 0;
    for (let i = 0; i < mvPx[0].length; i += 4) if (mvPx[0][i] !== mvPx[1][i] || mvPx[0][i + 1] !== mvPx[1][i + 1] || mvPx[0][i + 2] !== mvPx[1][i + 2]) lr++;
    out.lr_diff_pct = +(100 * lr / (w * h)).toFixed(2);
    // every material drawn this pass: its multiview program
    const progs = new Map();
    scene.traverse((o) => {
      const ms = o.material ? (Array.isArray(o.material) ? o.material : [o.material]) : [];
      for (const m of ms) {
        const p = renderer.properties.get(m);
        if (!p.programs) continue;
        for (const [key, prog] of p.programs) {
          if (!/,mv\d+$/.test(key) || progs.has(prog)) continue;
          const link = gl.getProgramParameter(prog.program, gl.LINK_STATUS);
          progs.set(prog, { name: m.name || (m.userData && m.userData.kind) || prog.name || m.type, type: m.type, link,
            log: link ? '' : (gl.getProgramInfoLog(prog.program) || '').slice(0, 400) });
        }
      }
    });
    out.programs = [...progs.values()];
    out.programs_failed = out.programs.filter((p) => !p.link).length;
    // each view = that eye's own render (the same programs' math, one pass): pixel-exact on the box's GPU
    out.ok = out.status === 'complete' && out.views.every((v) => v.drawn > 0 && v.diff_px === 0) && out.programs_failed === 0;
    return out;
  } catch (e) {
    out.error = String(e && e.stack || e);
    return out;
  } finally {
    renderer.setRenderTarget(prevRt);
    renderer.xr.enabled = xrOn;
    renderer.info.autoReset = autoReset;
    rt.dispose(); ref.dispose();
    gl.deleteFramebuffer(fb); gl.deleteFramebuffer(readFb); gl.deleteTexture(color); gl.deleteTexture(depth);
    renderer.state.bindFramebuffer(gl.FRAMEBUFFER, null);
    renderer.setRenderTarget(prevRt);
  }
}
